import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "recent-searches";
const MAX_ITEMS = 8;

const read = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
};

const write = (items: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error("Error saving recent searches:", error);
  }
};

/**
 * Tracks the user's most recent search terms in localStorage. Newest first,
 * de-duplicated (case-insensitive) and capped at MAX_ITEMS.
 */
export function useRecentSearches() {
  const [recent, setRecent] = useState<string[]>(read);

  // Keep state in sync if another tab/instance mutated storage.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setRecent(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add = useCallback((term: string) => {
    const value = term.trim();
    if (!value) return;
    setRecent((prev) => {
      const next = [value, ...prev.filter((x) => x.toLowerCase() !== value.toLowerCase())].slice(
        0,
        MAX_ITEMS,
      );
      write(next);
      return next;
    });
  }, []);

  const remove = useCallback((term: string) => {
    setRecent((prev) => {
      const next = prev.filter((x) => x !== term);
      write(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setRecent([]);
    write([]);
  }, []);

  return { recent, add, remove, clear };
}
